import React from 'react';

import { IconStarFilled } from '@xingternal/icons';
import { Marker } from '@xingternal/marker';

import { Salary } from '@/components/salary-metadata';
import {
  JobMatchingHighlightsV2Fragment,
  Maybe,
  JobTeaserVisibleJobFragment as RecommendationType,
  SimilarJobVisibleJobFragment as SimilarJobType,
} from '@/graphql/generated';

import { FactMarker, hasEmploymentTypeFact, hasSalaryFact, selectJobData } from '../../job-teaser-helpers';

import { MarkerContainer } from '../shared.styles';

type Props = {
  job: RecommendationType | SimilarJobType;
  matchingHighlights?: Maybe<JobMatchingHighlightsV2Fragment>;
  showEmploymentType?: boolean;
  showRatingMarker?: boolean;
};

export const JobTeaserFacts: React.FC<Props> = ({ job, matchingHighlights, showEmploymentType, showRatingMarker }) => {
  const { salary, showSalary, ratingAverage, employmentTypeValue } = selectJobData(job);

  const includesEmploymentTypeFact =
    matchingHighlights &&
    (hasEmploymentTypeFact(matchingHighlights.matchingFacts) ||
      hasEmploymentTypeFact(matchingHighlights.nonMatchingFacts));

  const showEmploymentTypeFromJob = showEmploymentType && employmentTypeValue && !includesEmploymentTypeFact;

  const includesSalaryFact =
    matchingHighlights &&
    (hasSalaryFact(matchingHighlights.matchingFacts) || hasSalaryFact(matchingHighlights.nonMatchingFacts));

  const showSalaryFromJob = salary && showSalary && !includesSalaryFact;

  const matchingFacts = matchingHighlights
    ? matchingHighlights.matchingFacts.map((fact, i) => <FactMarker key={i} variant="match" fact={fact} />)
    : null;

  const nonMatchingFacts = matchingHighlights
    ? matchingHighlights.nonMatchingFacts.map((fact, i) => <FactMarker key={i} variant="default" fact={fact} />)
    : null;

  const employmentTypeFromJob = showEmploymentTypeFromJob ? (
    <Marker variant="default">{employmentTypeValue}</Marker>
  ) : null;

  const salaryFromJob = showSalaryFromJob ? (
    <Marker variant="default">
      <Salary salary={salary} />
    </Marker>
  ) : null;

  const rating =
    showRatingMarker && ratingAverage ? (
      <Marker icon={IconStarFilled} variant="default">
        {(Math.round(ratingAverage * 10) / 10).toFixed(1)}
      </Marker>
    ) : null;

  return (
    <MarkerContainer>
      {rating}
      {nonMatchingFacts}
      {matchingFacts}
      {employmentTypeFromJob}
      {salaryFromJob}
    </MarkerContainer>
  );
};
